import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Package } from "lucide-react";

interface VariationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (color: any, size: string) => void;
  variants: any[];
  initialColor?: any;
  initialSize?: string | null;
  originalColor?: string | null;
  originalSize?: string | null;
  productTitle: string;
  isLoading?: boolean;
}

const isValidImage = (url: any) => {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith('http') || url.startsWith('/') || url.startsWith('data:');
};

const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`tw-relative tw-overflow-hidden tw-bg-[#1c1c1e] tw-rounded-md ${className}`}>
    <motion.div
      className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-transparent tw-via-white/5 tw-to-transparent"
      initial={{ x: "-100%" }}
      animate={{ x: "100%" }}
      transition={{
        duration: 1.6, repeat: Infinity, ease: "easeInOut",
      }}
    />
  </div>
);

const VariationSelectionModal: React.FC<VariationSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  variants,
  initialColor,
  initialSize,
  originalColor,
  originalSize,
  productTitle,
  isLoading = false,
}) => {
  const [selectedColor, setSelectedColor] = React.useState<any>(null);
  const [selectedSize, setSelectedSize] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen && !isLoading) {
      setSelectedColor(initialColor || variants[0]?.colors?.[0] || null);
      setSelectedSize(initialSize || null);
    }
  }, [isOpen, initialColor, initialSize, variants, isLoading]);

  const handleConfirm = () => {
    if (selectedColor && selectedSize) {
      onConfirm(selectedColor, selectedSize);
    }
  };

  const colors = variants[0]?.colors || [];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="tw-fixed tw-inset-0 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="tw-absolute tw-inset-0 tw-bg-black/90 tw-backdrop-blur-xl"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            className="tw-relative tw-bg-[#0d0d0d] tw-rounded-[32px] tw-w-full tw-max-w-4xl tw-overflow-hidden tw-shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] tw-border tw-border-white/5"
          >
            <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-stretch">
              {/* Product Image Gallery Preview */}
              <div className="tw-w-full md:tw-w-[45%] tw-min-h-[400px] md:tw-h-auto tw-bg-[#1c1c1e] tw-relative tw-overflow-hidden">
                {isLoading ? (
                  <Skeleton className="tw-w-full tw-h-full tw-rounded-none" />
                ) : isValidImage(selectedColor?.image) ? (
                  <motion.img 
                    key={selectedColor.name}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    src={selectedColor.image} 
                    alt={productTitle} 
                    className="tw-w-full tw-h-full tw-object-cover" 
                  />
                ) : (
                  <div className="tw-bg-[#1c1c1e] tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                    <Package className="tw-text-gray-700 tw-w-16 tw-h-16" />
                  </div>
                )}
                
                {/* Floating "Replacement" Tag */}
                <div className="tw-absolute tw-top-6 tw-left-6 tw-bg-black/60 tw-backdrop-blur-xl tw-border tw-border-white/10 tw-px-4 tw-py-2 tw-rounded-full tw-z-10">
                  <span className="tw-text-[10px] tw-font-bold tw-text-white tw-tracking-widest tw-uppercase tw-opacity-90">Selected Replacement</span>
                </div>
              </div>

              {/* Selection Interface */}
              <div className="tw-flex-1 tw-p-8 md:tw-p-10 tw-flex tw-flex-col">
                <div className="tw-flex tw-justify-between tw-items-start tw-mb-8">
                  <div className="tw-space-y-1">
                    {isLoading ? (
                      <>
                        <Skeleton className="tw-h-8 tw-w-64" />
                        <Skeleton className="tw-h-4 tw-w-48" />
                      </>
                    ) : (
                      <>
                        <h2 className="tw-text-2xl tw-font-bold tw-text-white tw-tracking-tight">{productTitle}</h2>
                        <p className="tw-text-gray-500 tw-text-sm">Choose your preferred variation for the replacement.</p>
                      </>
                    )}
                  </div>
                  <button onClick={onClose} className="tw-p-2 tw-text-gray-500 hover:tw-text-white tw-bg-transparent tw-border-none tw-cursor-pointer">
                    <X className="tw-w-6 tw-h-6" />
                  </button>
                </div>

                <div className="tw-space-y-10 tw-flex-1">
                   {!isLoading && colors.length === 0 ? (
                     <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-py-20 tw-text-center">
                        <Package className="tw-w-16 tw-h-16 tw-text-gray-800 tw-mb-4" />
                        <h3 className="tw-text-xl tw-font-bold tw-text-white">Out of Stock</h3>
                        <p className="tw-text-gray-500 tw-text-sm tw-max-w-xs tw-mt-2">Unfortunately, there are no variations available for this product at the moment.</p>
                     </div>
                   ) : (
                     <>
                       {/* Color Selection */}
                       <div className="tw-space-y-4">
                         <div className="tw-flex tw-justify-between tw-items-center">
                           <p className="tw-text-[10px] tw-font-black tw-text-gray-400 tw-uppercase tw-tracking-[0.2em]">Select Color</p>
                           {!isLoading && selectedColor && <span className="tw-text-[#00C5FB] tw-text-xs tw-font-bold">{selectedColor.name}</span>}
                         </div>
                         <div className="tw-flex tw-flex-wrap tw-gap-3">
                            {isLoading ? (
                              [1, 2, 3, 4].map(i => (
                                <Skeleton key={i} className="tw-w-14 tw-h-14 tw-rounded-xl" />
                              ))
                            ) : (
                              colors.map((color: any) => (
                                <button 
                                  key={color.name} 
                                  onClick={() => { setSelectedColor(color); setSelectedSize(null); }} 
                                  className={`tw-group tw-relative tw-w-14 tw-h-14 tw-rounded-xl tw-overflow-hidden tw-border-2 tw-transition-all tw-p-0 tw-cursor-pointer ${selectedColor?.name === color.name ? 'tw-border-[#00C5FB] tw-ring-4 tw-ring-[#00C5FB]/10' : 'tw-border-transparent hover:tw-border-gray-600'} ${originalColor && color.name.toLowerCase() === originalColor.toLowerCase() ? 'tw-border-dashed tw-border-[#00C5FB]/40' : ''}`}
                                >
                                  <img src={color.image} alt={color.name} className="tw-w-full tw-h-full tw-object-cover" />
                                  <div className={`tw-absolute tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-transition-opacity ${selectedColor?.name === color.name ? 'tw-opacity-100' : 'tw-opacity-0 group-hover:tw-opacity-100'}`}>
                                    <Check className="tw-w-5 tw-h-5 tw-text-[#00C5FB]" strokeWidth={3} />
                                  </div>
                                  {originalColor && color.name.toLowerCase() === originalColor.toLowerCase() && (
                                    <div className="tw-absolute tw-top-1 tw-right-1 tw-w-2 tw-h-2 tw-bg-[#00C5FB] tw-rounded-full tw-shadow-[0_0_8px_rgba(0,197,251,0.8)] tw-z-10" />
                                  )}
                                </button>
                              ))
                            )}
                         </div>
                       </div>

                       {/* Size Selection */}
                       <div className="tw-space-y-4">
                          <p className="tw-text-[10px] tw-font-black tw-text-gray-400 tw-uppercase tw-tracking-[0.2em]">Select Size</p>
                          <div className="tw-flex tw-flex-wrap tw-gap-2">
                            {isLoading ? (
                               [1, 2, 3, 4, 5].map(i => (
                                 <Skeleton key={i} className="tw-h-10 tw-w-16 tw-rounded-xl" />
                               ))
                            ) : (
                              selectedColor && Object.keys(selectedColor.sizes).map(sz => (
                                <button 
                                  key={sz} 
                                  onClick={() => setSelectedSize(sz)} 
                                  className={`tw-relative tw-px-5 tw-py-2.5 tw-rounded-xl tw-text-xs tw-font-bold tw-transition-all tw-border-2 tw-cursor-pointer ${selectedSize === sz ? 'tw-border-[#00C5FB] tw-bg-[#00C5FB] tw-text-black' : 'tw-border-gray-800 tw-bg-[#1c1c1e] tw-text-gray-400 hover:tw-border-gray-600'} ${originalSize && sz.toLowerCase() === originalSize.toLowerCase() ? 'tw-ring-2 tw-ring-[#00C5FB]/40 tw-ring-offset-2 tw-ring-offset-black tw-border-dashed' : ''}`}
                                >
                                  {sz}
                                  {originalSize && sz.toLowerCase() === originalSize.toLowerCase() && (
                                    <div className="tw-absolute tw-top-1.5 tw-right-1.5 tw-w-2 tw-h-2 tw-bg-[#00C5FB] tw-rounded-full tw-shadow-[0_0_10px_rgba(0,197,251,0.9)] tw-z-20" />
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                       </div>
                     </>
                   )}
                </div>

                <div className="tw-mt-10 tw-pt-8 tw-border-t tw-border-white/5 tw-flex tw-gap-4">
                  <button onClick={onClose} className="tw-flex-1 tw-py-4 tw-rounded-xl tw-bg-transparent tw-text-gray-500 tw-font-bold tw-text-sm tw-border tw-border-gray-800 tw-cursor-pointer hover:tw-bg-gray-800 tw-transition-all">
                    {colors.length === 0 ? "Go Back" : "Cancel"}
                  </button>
                  {colors.length > 0 && (
                    <button 
                      onClick={handleConfirm} 
                      disabled={isLoading || !selectedColor || !selectedSize} 
                      className="tw-flex-[2] tw-py-4 tw-rounded-xl tw-bg-[#00C5FB] tw-text-black tw-font-bold tw-text-sm tw-border-none tw-cursor-pointer hover:tw-bg-cyan-400 tw-transition-all tw-shadow-[0_0_20px_rgba(0,197,251,0.3)] disabled:tw-opacity-30 disabled:tw-cursor-not-allowed"
                    >
                      Confirm Replacement
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default VariationSelectionModal;
